import { Request } from '@mam/main/ApiClient';

export interface InternetAddress {
	readonly emailAddress: string;
	readonly personal: string | null;
}

export interface GenericNotification<Date> {
	readonly authenticatedRecipient: GenericAuthenticatedRecipient<Date>;
	readonly canceledTime: Date | null;
	readonly createdTime: Date;
	readonly deduplicationId: string;
	readonly email: NotificationEmail | null;
	readonly externalId: string;
	readonly id: string;
	readonly notificationTypeId: string;
	readonly perex: string | null;
	readonly recipients: NotificationRecipients[];
	readonly sender: string;
}

export type Notification = GenericNotification<Date>;

export interface NotificationEmail {
	readonly attachments: NotificationEmailAttachments[];
	readonly bcc: InternetAddress[];
	readonly cc: InternetAddress[];
	readonly externalId: string;
	readonly from: InternetAddress;
	readonly id: string;
	readonly replyTo: InternetAddress;
	readonly status: StatusEnum;
	readonly subject: string;
	readonly to: InternetAddress[];
}

export enum NotificationPriority {
	Normal = 'normal',
	High = 'high',
}

export type AuthenticatedRecipient = GenericAuthenticatedRecipient<Date>;

export interface GenericAuthenticatedRecipient<Date> {
	readonly isRead: boolean;
	readonly priority: NotificationPriority;
	readonly readTime: Date | null;
}

export enum StatusEnum {
	Created = 'created',
	Sent = 'sent',
	Failed = 'failed',
}

export interface NotificationEmailAttachments {
	readonly fileName: string;
	readonly id: string;
}

export interface NotificationRecipients {
	readonly email: string;
	readonly externalId: string;
	readonly phoneNumber: string;
}

export interface NotificationResponse {
	readonly notification: Notification;
}

export interface EncodedNotificationResponse {
	readonly notification: GenericNotification<string>;
}

export enum NotificationError {
	notFound = 'notification-not-found',
}

export const mapNotification = (notification: GenericNotification<string>) => ({
	...notification,
	authenticatedRecipient: {
		...notification.authenticatedRecipient,
		isRead: notification.authenticatedRecipient.readTime !== null,
		readTime:
			notification.authenticatedRecipient.readTime !== null
				? new Date(notification.authenticatedRecipient.readTime)
				: null,
	},
	canceledTime: notification.canceledTime !== null ? new Date(notification.canceledTime) : null,
	createdTime: new Date(notification.createdTime),
});

export class NotificationRequest implements Request<NotificationResponse, EncodedNotificationResponse> {
	public readonly api = 'pigeon';
	public readonly knownErrorCodes = Object.values(NotificationError);
	public readonly path: string;

	public constructor(notificationId: string) {
		this.path = `notifications/${notificationId}`;
	}

	public decode(response: EncodedNotificationResponse): NotificationResponse {
		return {
			notification: mapNotification(response.notification),
		};
	}
}
